/** @format */

import { useEffect, useRef } from "react";

/**
 * Scroll-driven "load more" for a scroll container.
 *
 * Why scroll-based instead of IntersectionObserver: an observer re-fires its
 * initial callback every time it is (re)attached, which made loading cascade
 * through every page at once and even fire on a plain re-render (e.g. clicking a
 * chat). A throttled scroll check only runs in response to a real user scroll, so:
 *   - clicking an item (no scroll) never triggers a load,
 *   - mounting / re-rendering / selecting an item never triggers a load,
 *   - exactly one page loads per edge hit (guarded by a latch),
 *   - if still at the edge after append/prepend, loading continues automatically.
 *
 * It deliberately does NOT auto-fill a tall viewport by chain-loading pages:
 * that produced a burst of `offset=…` requests that read like polling and
 * caused a visible glitch. Loading is driven solely by the user scrolling.
 *
 * `rootRef` is the scrollable element. `edge` controls whether we load when the
 * user reaches the `bottom` (default) or `top` edge.
 * `observeKey` should change when list length changes.
 * `resetKey` lets a parent cancel any pending continuation (e.g. on item select).
 * The returned ref is kept for API compatibility (rendered as a sentinel) but is
 * not required for the scroll logic.
 */
export function useIntersectionLoadMore({
  enabled = true,
  isLoading = false,
  onLoadMore,
  rootRef,
  observeKey,
  edge = "bottom",
  resetKey,
}) {
  const sentinelRef = useRef(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const enabledRef = useRef(enabled);
  const isLoadingRef = useRef(isLoading);
  /** Latch: true once we've asked for a page, reset when that load finishes. */
  const requestedRef = useRef(false);
  /** If a load was initiated by scrolling near edge, keep loading while still near edge. */
  const continueFromEdgeRef = useRef(false);

  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    // When a load finishes, release the latch so the next scroll can load again.
    if (!isLoading) requestedRef.current = false;
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // Selecting an item should never chain additional list loads.
  useEffect(() => {
    requestedRef.current = false;
    continueFromEdgeRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    const root = rootRef?.current;
    if (!root) return undefined;

    const THRESHOLD = 300;
    let frame = 0;

    const isNearEdge = () => {
      if (edge === "top") {
        return root.scrollTop <= THRESHOLD;
      }
      return root.scrollTop + root.clientHeight >= root.scrollHeight - THRESHOLD;
    };

    const requestMore = () => {
      requestedRef.current = true;
      continueFromEdgeRef.current = true;
      onLoadMoreRef.current?.();
    };

    const check = () => {
      frame = 0;
      if (!enabledRef.current || isLoadingRef.current || requestedRef.current) return;
      if (isNearEdge()) {
        requestMore();
      }
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(check);
    };

    root.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      root.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [rootRef, enabled, observeKey, edge]);

  useEffect(() => {
    if (isLoading || !enabled || !continueFromEdgeRef.current) return undefined;
    const root = rootRef?.current;
    if (!root) {
      continueFromEdgeRef.current = false;
      return undefined;
    }

    const THRESHOLD = 300;
    let frame = window.requestAnimationFrame(() => {
      if (!enabledRef.current || isLoadingRef.current || requestedRef.current) return;

      const nearEdge =
        edge === "top"
          ? root.scrollTop <= THRESHOLD
          : root.scrollTop + root.clientHeight >= root.scrollHeight - THRESHOLD;

      if (nearEdge) {
        requestedRef.current = true;
        onLoadMoreRef.current?.();
      } else {
        continueFromEdgeRef.current = false;
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isLoading, enabled, observeKey, rootRef, edge]);

  return sentinelRef;
}
